import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  Container,
  Box,
  Typography,
  TextField,
  Button,
  Alert,
  Paper,
  FormControlLabel,
  Checkbox,
  useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import SchoolIcon from '@mui/icons-material/School';
import TermsModal from '../components/TermsModal';
import PrivacyPolicyModal from '../components/PrivacyPolicyModal';
import { createUserDocument } from '../data/users';
import GradeLevelSelector from '../components/GradeLevelSelector';
import { GradeLevel, DifficultyLevel } from '../models/User';

declare global {
  interface Window {
    grecaptcha: {
      ready: (callback: () => void) => void;
      execute: (siteKey: string, options: { action: string }) => Promise<string>;
    };
  }
}

export default function SignUpPage() {
  const [step, setStep] = useState<1 | 2>(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [isRecaptchaReady, setIsRecaptchaReady] = useState(false);
  const [gradeLevel, setGradeLevel] = useState<GradeLevel | null>(null);
  const [difficultyLevel, setDifficultyLevel] = useState<DifficultyLevel | null>(null);
  const [isCreatingAccount, setIsCreatingAccount] = useState(false);
  const { signup } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    // Initialize reCAPTCHA
    const script = document.createElement('script');
    script.src = `https://www.google.com/recaptcha/api.js?render=${import.meta.env.VITE_RECAPTCHA_SITE_KEY}`;
    script.async = true;
    script.defer = true;
    document.head.appendChild(script);

    script.onload = () => {
      window.grecaptcha.ready(() => {
        setIsRecaptchaReady(true);
      });
    };

    return () => {
      document.head.removeChild(script);
    };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isRecaptchaReady) {
      setError('reCAPTCHA is not ready yet. Please try again in a moment.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!termsAccepted || !privacyAccepted) {
      setError('Please accept both the Terms and Conditions and Privacy Policy to continue');
      return;
    }

    setError('');
    setStep(2);
  };

  const handleFinalSubmit = async () => {
    if (!gradeLevel || !difficultyLevel) return;

    setIsCreatingAccount(true);
    try {
      const userCredential = await signup(email, password);
      const user = userCredential.user;

      await createUserDocument(
        user.uid,
        email,
        email.split('@')[0],
        termsAccepted,
        privacyAccepted,
        {
          dailyLimit: 10,
          cardsGeneratedToday: 0,
          lastGenerationDate: new Date().toISOString(),
        },
        gradeLevel,
        difficultyLevel
      );

      navigate('/verify-email');
    } catch (err) {
      setStep(1);
      if (err instanceof Error) {
        setError(`Failed to create account: ${err.message}`);
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setIsCreatingAccount(false);
    }
  };

  const handleTermsAccept = () => {
    setTermsAccepted(true);
    setShowTermsModal(false);
  };

  const handlePrivacyAccept = () => {
    setPrivacyAccepted(true);
    setShowPrivacyModal(false);
  };

  const clayInputSx = {
    '& .MuiOutlinedInput-root': {
      borderRadius: 1.5,
      background: theme.palette.mode === 'dark'
        ? alpha(theme.palette.common.white, 0.04)
        : alpha(theme.palette.common.white, 0.6),
      backdropFilter: 'blur(4px)',
      '&.Mui-focused': {
        boxShadow: `0 0 0 3px ${alpha(theme.palette.primary.main, 0.15)}`,
        '& .MuiOutlinedInput-notchedOutline': { borderWidth: 2 },
      },
    },
  };

  return (
    <Box
      sx={{
        position: 'relative',
        minHeight: '100vh',
        overflow: 'hidden',
        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.main} 100%)`,
        display: 'flex',
        alignItems: 'center',
        py: { xs: 6, md: 8 },
      }}
    >
      {/* Ambient light blobs */}
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at 25% 20%, ${alpha(theme.palette.common.white, 0.16)}, transparent 45%)`,
          filter: 'blur(60px)',
          opacity: 0.8,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at 78% 12%, ${alpha(theme.palette.common.white, 0.12)}, transparent 38%)`,
          filter: 'blur(70px)',
          opacity: 0.65,
          pointerEvents: 'none',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          background: `radial-gradient(ellipse at 60% 85%, ${alpha(theme.palette.secondary.dark, 0.35)}, transparent 40%)`,
          filter: 'blur(80px)',
          pointerEvents: 'none',
        }}
      />

      <Helmet>
        <title>Sign Up - BrainLoop</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>

      <Container maxWidth="xs" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Brand header above card */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mb: 4,
          }}
        >
          {/* Accent pill */}
          <Box
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              px: 1.5,
              py: 0.5,
              mb: 2,
              borderRadius: 99,
              background: alpha(theme.palette.common.white, 0.15),
              border: `1px solid ${alpha(theme.palette.common.white, 0.25)}`,
              backdropFilter: 'blur(8px)',
            }}
          >
            <Typography variant="caption" sx={{ color: 'primary.contrastText', fontWeight: 600, letterSpacing: 0.5 }}>
              ✦ AI-Powered Learning
            </Typography>
          </Box>

          {/* Icon orb + title */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 1 }}>
            <Box
              sx={{
                width: 52,
                height: 52,
                borderRadius: '50%',
                background: alpha(theme.palette.common.white, 0.2),
                backdropFilter: 'blur(8px)',
                border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                boxShadow: [
                  `0 8px 24px ${alpha(theme.palette.primary.dark, 0.5)}`,
                  `inset 0 2px 0 ${alpha(theme.palette.common.white, 0.35)}`,
                  `inset 0 -2px 0 ${alpha(theme.palette.common.black, 0.1)}`,
                ].join(', '),
              }}
            >
              <SchoolIcon sx={{ fontSize: 28, color: 'primary.contrastText' }} />
            </Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{ fontWeight: 700, color: 'primary.contrastText', letterSpacing: 0.5 }}
            >
              BrainLoop
            </Typography>
          </Box>

          <Typography
            variant="body1"
            sx={{ color: alpha(theme.palette.common.white, 0.8), textAlign: 'center' }}
          >
            Your AI-Powered Study Companion
          </Typography>
        </Box>

        {/* Clay card */}
        <Paper
          elevation={0}
          sx={{
            p: 4,
            width: '100%',
            borderRadius: 4,
            background: theme.palette.mode === 'dark'
              ? alpha(theme.palette.background.default, 0.82)
              : alpha(theme.palette.background.paper, 0.9),
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: theme.palette.mode === 'dark'
              ? `1px solid ${alpha(theme.palette.common.white, 0.1)}`
              : `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
            boxShadow: theme.palette.mode === 'dark'
              ? [
                  `0 20px 60px ${alpha(theme.palette.common.black, 0.5)}`,
                  `0 8px 20px ${alpha(theme.palette.common.black, 0.3)}`,
                  `inset 0 2px 0 ${alpha(theme.palette.common.white, 0.05)}`,
                  `inset 0 -2px 0 ${alpha(theme.palette.common.black, 0.2)}`,
                ].join(', ')
              : [
                  `0 20px 60px ${alpha(theme.palette.primary.main, 0.2)}`,
                  `0 8px 20px ${alpha(theme.palette.common.black, 0.08)}`,
                  `inset 0 2px 0 ${alpha(theme.palette.common.white, 0.95)}`,
                  `inset 0 -2px 0 ${alpha(theme.palette.primary.main, 0.08)}`,
                ].join(', '),
          }}
        >
          <Typography
            component="h2"
            variant="h5"
            align="center"
            gutterBottom
            sx={{ fontWeight: 600, color: 'primary.main', mb: 2 }}
          >
            {step === 1 ? 'Create Your Account' : 'Personalize Your Learning'}
          </Typography>
          {step === 1 && (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              Step 1 of 2
            </Typography>
          )}
          {step === 2 && (
            <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 2 }}>
              Step 2 of 2 — This helps the AI tailor content to your level
            </Typography>
          )}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {step === 2 && (
            <GradeLevelSelector
              gradeLevel={gradeLevel}
              difficultyLevel={difficultyLevel}
              onGradeLevelChange={setGradeLevel}
              onDifficultyLevelChange={setDifficultyLevel}
              onSubmit={handleFinalSubmit}
              submitLabel="Create Account"
              isLoading={isCreatingAccount}
            />
          )}
          {step === 1 && (
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email Address"
              name="email"
              autoComplete="email"
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              sx={{ ...clayInputSx, mb: 0 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type="password"
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ ...clayInputSx, mb: 0 }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="confirm-password"
              label="Confirm Password"
              type="password"
              id="confirm-password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ ...clayInputSx, mb: 0 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={termsAccepted}
                  onChange={() => setShowTermsModal(true)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I accept the{' '}
                  <Button
                    onClick={() => setShowTermsModal(true)}
                    sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                  >
                    Terms and Conditions
                  </Button>
                </Typography>
              }
              sx={{ mt: 2 }}
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={privacyAccepted}
                  onChange={() => setShowPrivacyModal(true)}
                  color="primary"
                />
              }
              label={
                <Typography variant="body2">
                  I accept the{' '}
                  <Button
                    onClick={() => setShowPrivacyModal(true)}
                    sx={{ textTransform: 'none', p: 0, minWidth: 'auto' }}
                  >
                    Privacy Policy
                  </Button>
                </Typography>
              }
              sx={{ mt: 1 }}
            />
            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              sx={(t) => ({
                mt: 3,
                mb: 2,
                py: 1.5,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: 'subtitle1.fontSize',
                backgroundImage: `linear-gradient(135deg, ${t.palette.primary.main}, ${t.palette.primary.dark})`,
                color: 'primary.contrastText',
                boxShadow: [
                  `0 6px 20px ${alpha(t.palette.primary.main, 0.4)}`,
                  `inset 0 2px 0 ${alpha(t.palette.common.white, 0.2)}`,
                  `inset 0 -2px 0 ${alpha(t.palette.common.black, 0.12)}`,
                ].join(', '),
                transition: 'transform 0.15s ease, box-shadow 0.15s ease',
                '&:hover': {
                  backgroundImage: `linear-gradient(135deg, ${t.palette.primary.light}, ${t.palette.primary.main})`,
                  boxShadow: [
                    `0 8px 28px ${alpha(t.palette.primary.main, 0.5)}`,
                    `inset 0 2px 0 ${alpha(t.palette.common.white, 0.25)}`,
                    `inset 0 -2px 0 ${alpha(t.palette.common.black, 0.12)}`,
                  ].join(', '),
                  transform: 'translateY(-1px)',
                },
                '&:disabled': {
                  backgroundImage: 'none',
                },
              })}
              disabled={!termsAccepted || !privacyAccepted}
            >
              Create Account
            </Button>
            <Box sx={{ textAlign: 'center' }}>
              <Link to="/login" style={{ textDecoration: 'none' }}>
                <Typography
                  color="primary"
                  variant="body2"
                  sx={{
                    fontWeight: 500,
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  Already have an account? Sign in
                </Typography>
              </Link>
            </Box>
          </Box>
          )}
        </Paper>
      </Container>

      <TermsModal
        open={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={handleTermsAccept}
      />
      <PrivacyPolicyModal
        open={showPrivacyModal}
        onClose={() => setShowPrivacyModal(false)}
        onAccept={handlePrivacyAccept}
      />
    </Box>
  );
} 